import { Injectable, inject } from '@angular/core';
import { lastValueFrom } from 'rxjs';

import { VehicleService } from '../../../../api';
import { VehicleDetailDto } from '../../types/VehicleDetailDto';
import { VehicleDto } from '../../types/VehicleDto';
import {
  LocalStorageItem,
  getLocalStorageItem,
  setLocalStorageItem,
} from '../../utils';

@Injectable({
  providedIn: 'root',
})
export class CustomVehicleService {
  private readonly vehicleService = inject(VehicleService);

  public async vehicleListAllCached(
    overrideCache = false,
  ): Promise<VehicleDto[]> {
    const vehicleListAllCache = getLocalStorageItem(
      LocalStorageItem.VehicleListAll,
    );

    if (vehicleListAllCache != null && !overrideCache) {
      return Promise.resolve(vehicleListAllCache) as Promise<VehicleDto[]>;
    }

    // A driver without the `vehicleList` right gets a 403 here. Signing in to a
    // vehicle needs no right at all, so such users now reach the portal via /tag
    // — and an unhandled rejection would abort the rest of initializeAppData().
    try {
      const result = await lastValueFrom(this.vehicleService.vehicleListAll());
      if (!('result' in result)) {
        setLocalStorageItem(LocalStorageItem.VehicleListAll, result);
        return result;
      }
    } catch (e) {
      console.error(e);
    }

    return [];
  }

  public setOneVehicleInCache(
    vehicleToSet: VehicleDetailDto | VehicleDto | null,
  ): void {
    if (vehicleToSet) {
      const vehicleListAllCache = getLocalStorageItem<VehicleDto[]>(
        LocalStorageItem.VehicleListAll,
      );

      if (vehicleListAllCache != null) {
        const vehicleListAllCacheUpdated = vehicleListAllCache.map(
          (currentVehicle) =>
            currentVehicle.id === vehicleToSet.id
              ? {
                  ...currentVehicle,
                  ...vehicleToSet,
                }
              : currentVehicle,
        );
        setLocalStorageItem(
          LocalStorageItem.VehicleListAll,
          vehicleListAllCacheUpdated,
        );
      }
    }
  }

  public async vehiclesGetAndCache(): Promise<VehicleDto[]> {
    const result = await lastValueFrom(this.vehicleService.vehicleListAll());

    if (!('result' in result)) {
      setLocalStorageItem(LocalStorageItem.VehicleListAll, result);
      return result as VehicleDto[];
    }

    return [];
  }
}
